"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { useDebounceValue } from "usehooks-ts";
import {
  Autocomplete,
  Button,
  Collection,
  Disclosure,
  DisclosurePanel,
  Header,
  Heading,
  Input,
  ListBox as RACListBox,
  ListBoxItem,
  ListBoxSection,
  Popover,
  Select as RACSelect,
  SearchField,
  Switch,
  Tabs,
  type Key,
} from "react-aria-components";
import { useFilter } from "react-aria";
import clsx from "clsx";

import { logger } from "@utilities/logger";
import IconChevronDownSmall from "@ui/central-icons/icons/IconChevronDownSmall";
import IconCheckmark2Small from "@ui/central-icons/icons/IconCheckmark2Small";
import IconMagnifyingGlass2 from "@ui/central-icons/icons/IconMagnifyingGlass2";
import IconPlusSmall from "@ui/central-icons/icons/IconPlusSmall";
import IconMinusSmall from "@ui/central-icons/icons/IconMinusSmall";
import { iconStrokeFix } from "@ui/central-icons/iconUtils";
import { TabList } from "@ui/components/core/TabList";
import { calculateLlmPrices, type LlmPriceEntry } from "@/app/_api/elevenlabs/llmPrice";
import { formatPricePerMinute } from "@utilities/numbers";

import { type CalculatorMode, usePricingTableContext } from "../PricingTableContext";
import { BillingCycleToggle } from "../BillingCycleToggle";
import { UsageSlider } from "./UsageSlider";
import { getAgentsMaxIncludedMinutes, useUsageSlider } from "./calculatorUtils";
import { featuredByPrice, groupedByProvider, modelLabel, pickDefaultModel } from "./agentModels";
import { StepIndicator } from "./StepIndicator";
import { AgentsCalculatorResultCard } from "./AgentsCalculatorResultCard";

// Scenario defaults — mirror the baseline scenario in llmPrice.ts, but kept here so this
// client component never imports the server fetch helper.
const DEFAULT_PROMPT_LENGTH = 500;
const DEFAULT_KB_PAGES = 0;
// RAG defaults off — ~90% of workspaces run without it. It only does anything once the knowledge
// base is large enough that retrieval beats stuffing the whole thing into every prompt, so below
// RAG_MIN_PAGES the toggle is hidden and `rag_enabled: false` is sent regardless — matching the
// real product, where the option only appears for sizeable knowledge bases (and where on/off
// prices are identical at small sizes).
const DEFAULT_RAG_ENABLED = false;
const RAG_MIN_PAGES = 10;
const DEFAULT_MINUTES_PER_MONTH = 270;

// Slider ceilings (the slider snaps to nice values below these — see sliderToValue).
const PROMPT_LENGTH_MAX = 5000;
const KB_PAGES_MAX = 500;

const DEBOUNCE_MS = 300;

interface ModelSelectProps {
  prices: LlmPriceEntry[];
  selectedModel: string;
  onSelect: (model: string) => void;
  ariaLabel: string;
  placeholder: string;
  searchPlaceholder: string;
  emptyLabel: string;
  perMinuteSuffix: string;
}

/**
 * React Aria single-select over the priced models — grouped by provider with each model's LLM $/min
 * shown inline, plus a type-to-filter search box pinned at the top of the popover (mirrors the in-app
 * LLM selector). The trigger keeps showing the chosen model + its price while the user searches: its
 * value is resolved from the full price list, independent of the (filtered) listbox. Built on raw RAC
 * primitives — Select + Autocomplete + SearchField + grouped ListBox — since the core ListBox owns its
 * own popover and can't host a search field inside it.
 */
function ModelSelect({
  prices,
  selectedModel,
  onSelect,
  ariaLabel,
  placeholder,
  searchPlaceholder,
  emptyLabel,
  perMinuteSuffix,
}: ModelSelectProps) {
  const { contains } = useFilter({ sensitivity: "base" });
  const groups = useMemo(() => groupedByProvider(prices), [prices]);

  const selected = prices.find(p => p.llm === selectedModel);
  const selectedPrice = selected ? `$${formatPricePerMinute(selected.pricePerMinute)}` : undefined;

  return (
    <RACSelect
      aria-label={ariaLabel}
      selectedKey={selectedModel || null}
      onSelectionChange={(key: Key | null) => {
        if (key !== null) onSelect(String(key));
      }}
    >
      <Button
        className={({ isFocusVisible }) =>
          clsx(
            "tw-flex tw-w-full tw-items-center tw-justify-between tw-gap-2 tw-h-12 tw-px-4 tw-bg-white tw-border tw-border-gray-200 tw-rounded-xl tw-type-base tw-text-black tw-transition-colors hover:tw-border-gray-300 tw-cursor-pointer",
            isFocusVisible ? "tw-outline-focus" : "tw-outline-none",
          )
        }
      >
        <span className='tw-flex-1 tw-min-w-0 tw-truncate tw-text-left'>
          {selected ? modelLabel(selected.llm) : placeholder}
        </span>
        <span className='tw-flex tw-flex-none tw-items-center tw-gap-2'>
          {selectedPrice && (
            <span className='tw-tabular-nums tw-text-gray-500'>
              {selectedPrice}
              {perMinuteSuffix}
            </span>
          )}
          <IconChevronDownSmall className={clsx("tw-size-4 tw-text-gray-400 tw-flex-none", iconStrokeFix)} />
        </span>
      </Button>
      <Popover
        offset={8}
        placement='bottom'
        className='tw-w-[--trigger-width] tw-flex tw-flex-col tw-overflow-hidden tw-rounded-2xl tw-bg-white tw-text-sm tw-text-black tw-transition tw-ease-out tw-duration-150 data-[entering]:tw-opacity-0 data-[exiting]:tw-opacity-0'
        style={{ boxShadow: "0 0 1px 0 rgb(0 0 0 / 0.4), 0 4px 4px rgb(0 0 0 / 0.04)" }}
      >
        <Autocomplete filter={contains}>
          <SearchField
            aria-label={searchPlaceholder}
            className='tw-flex tw-h-12 tw-flex-none tw-items-center tw-gap-2 tw-border-b tw-border-gray-100 tw-px-4'
          >
            <IconMagnifyingGlass2
              aria-hidden
              className={clsx("tw-size-4 tw-flex-none tw-text-gray-400", iconStrokeFix)}
            />
            <Input
              placeholder={searchPlaceholder}
              className='tw-flex-1 tw-min-w-0 tw-bg-transparent tw-outline-none tw-text-black placeholder:tw-text-gray-400 [&::-webkit-search-cancel-button]:tw-hidden'
            />
          </SearchField>
          <RACListBox
            aria-label={ariaLabel}
            selectionMode='single'
            className='tw-max-h-64 tw-flex-auto tw-overflow-auto tw-scrollbar-none tw-scroll-py-1.5 tw-p-1.5 tw-outline-none'
            renderEmptyState={() => <div className='tw-px-2.5 tw-py-3 tw-text-gray-500'>{emptyLabel}</div>}
          >
            {groups.map((group, groupIndex) => (
              <ListBoxSection key={group.id} id={group.id}>
                <Header
                  className={clsx(
                    "tw-px-2.5 tw-py-1 tw-text-xs tw-font-medium tw-text-gray-500",
                    groupIndex > 0 && "tw-mt-2",
                  )}
                >
                  {group.label}
                </Header>
                <Collection items={group.models}>
                  {model => (
                    <ListBoxItem
                      id={model.llm}
                      textValue={modelLabel(model.llm)}
                      className={({ isHovered, isFocusVisible }) =>
                        clsx(
                          "tw-flex tw-h-9 tw-items-center tw-justify-between tw-gap-4 tw-rounded-xl tw-px-2.5 tw-cursor-pointer tw-outline-none",
                          (isHovered || isFocusVisible) && "tw-bg-gray-50",
                          isFocusVisible && "tw-outline-focus",
                        )
                      }
                    >
                      {({ isSelected }) => (
                        <>
                          <span className='tw-truncate'>{modelLabel(model.llm)}</span>
                          <span className='tw-flex tw-flex-none tw-items-center tw-gap-1.5'>
                            <span className='tw-tabular-nums tw-text-gray-500'>
                              ${formatPricePerMinute(model.pricePerMinute)}
                              {perMinuteSuffix}
                            </span>
                            <IconCheckmark2Small
                              className={clsx("tw-size-4 tw-text-black", iconStrokeFix, !isSelected && "tw-invisible")}
                            />
                          </span>
                        </>
                      )}
                    </ListBoxItem>
                  )}
                </Collection>
              </ListBoxSection>
            ))}
          </RACListBox>
        </Autocomplete>
      </Popover>
    </RACSelect>
  );
}

interface RagToggleProps {
  isSelected: boolean;
  onChange: (isSelected: boolean) => void;
  label: string;
}

/** React Aria switch styled with tw-* classes (the repo's core/Switch is Radix — banned for new UI). */
function RagToggle({ isSelected, onChange, label }: RagToggleProps) {
  return (
    <Switch
      isSelected={isSelected}
      onChange={onChange}
      className='tw-group tw-flex tw-items-center tw-justify-between tw-gap-3 tw-h-12 tw-px-4 tw-bg-white tw-border tw-border-gray-200 tw-rounded-xl tw-type-base tw-text-black tw-cursor-pointer'
    >
      <span className='tw-flex-1 tw-text-left'>{label}</span>
      <span className='tw-relative tw-h-6 tw-w-10 tw-flex-none tw-rounded-full tw-bg-gray-200 tw-transition-colors group-data-[selected]:tw-bg-black group-data-[focus-visible]:tw-outline-focus'>
        <span className='tw-absolute tw-left-0.5 tw-top-0.5 tw-size-5 tw-rounded-full tw-bg-white tw-shadow tw-transition-transform group-data-[selected]:tw-translate-x-4' />
      </span>
    </Switch>
  );
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return <label className='tw-type-sm tw-font-medium tw-block tw-text-black'>{children}</label>;
}

/**
 * Collapsible "power-user" panel holding the LLM-cost knobs (model, system-prompt length, knowledge
 * base, RAG). Collapsed by default so a first-time visitor gets a simple minutes → recommended-plan
 * flow, with the finer agent configuration one click away. React Aria Disclosure (the repo's core
 * accordion/Switch are Radix — banned for new UI); mirrors {@link FieldLabel}'s tw-* styling.
 */
function AdvancedConfig({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Disclosure className='tw-group tw-rounded-xl tw-border tw-border-gray-200 tw-bg-white'>
      {({ isExpanded }) => (
        <>
          <Heading className='tw-m-0'>
            <Button
              slot='trigger'
              className={({ isFocusVisible }) =>
                clsx(
                  "tw-flex tw-w-full tw-items-center tw-justify-between tw-gap-2 tw-h-12 tw-px-4 tw-type-base tw-font-medium tw-text-black tw-cursor-pointer tw-rounded-xl",
                  isFocusVisible ? "tw-outline-focus" : "tw-outline-none",
                )
              }
            >
              {/* flex-1 + text-left: `tw-type-base` adds cap-trim ::before/::after pseudo-elements
                  which, on this flex row, count as flex items — letting the label grow absorbs the
                  free space so justify-between can't center it between those phantom items. */}
              <span className='tw-flex-1 tw-text-left'>{label}</span>
              {/* +/− badge (not a chevron) so it reads as an expand/collapse toggle, distinct from
                  the model select's dropdown chevron. */}
              <span
                aria-hidden
                className='tw-flex tw-size-7 tw-flex-none tw-items-center tw-justify-center tw-rounded-full tw-bg-gray-100 tw-text-black tw-transition-colors group-hover:tw-bg-gray-200'
              >
                {isExpanded ? (
                  <IconMinusSmall className={clsx("tw-size-4", iconStrokeFix)} />
                ) : (
                  <IconPlusSmall className={clsx("tw-size-4", iconStrokeFix)} />
                )}
              </span>
            </Button>
          </Heading>
          <DisclosurePanel
            className={clsx(
              "motion-safe:tw-transition-[height,opacity] motion-safe:tw-ease-in-out motion-safe:tw-duration-300 tw-overflow-clip",
              !isExpanded && "tw-opacity-0",
            )}
            style={{ height: "var(--disclosure-panel-height)" }}
          >
            <div className='tw-space-y-6 tw-px-4 tw-pb-4 tw-pt-1'>{children}</div>
          </DisclosurePanel>
        </>
      )}
    </Disclosure>
  );
}

export interface AgentsCalculatorProps {
  /** Server-fetched prices for the default scenario; seed the first client paint (component is `ssr: false`). */
  baselinePrices: LlmPriceEntry[];
  className?: string;
}

/**
 * Estimates the LLM cost of an ElevenAgents conversation. The user picks a model and tunes the
 * system-prompt length, knowledge-base size, RAG and monthly conversation minutes; the live
 * per-minute LLM price (USD) comes from the public agents pricing endpoint via the
 * {@link calculateLlmPrices} server action (debounced). The endpoint returns every model in one
 * call, so the model dropdown is pure client-side selection — only prompt/KB/RAG changes re-fetch.
 *
 * The result card mirrors the API calculator: "subscription" recommends the cheapest plan covering
 * the estimated minutes (with LLM cost as an add-on); "pay as you go" shows the estimated monthly
 * LLM cost. The payment-model + billing-cycle state is shared via {@link usePricingTableContext}.
 */
export function AgentsCalculator({ baselinePrices, className }: AgentsCalculatorProps) {
  const t = useTranslations("PricingClientKeys.AgentsCalculator");
  const { calculatorMode, setCalculatorMode, billingCycle } = usePricingTableContext();

  const [prices, setPrices] = useState<LlmPriceEntry[]>(baselinePrices);
  const [selectedModel, setSelectedModel] = useState<string>(() => pickDefaultModel(baselinePrices));
  const [ragEnabled, setRagEnabled] = useState(DEFAULT_RAG_ENABLED);
  // Seed loading when there's no baseline so the first client fetch shows progress, not an empty card.
  const [isLoading, setIsLoading] = useState(baselinePrices.length === 0);

  const promptSlider = useUsageSlider(DEFAULT_PROMPT_LENGTH, PROMPT_LENGTH_MAX);
  const kbSlider = useUsageSlider(DEFAULT_KB_PAGES, KB_PAGES_MAX);
  // Let the minutes slider reach ~1.5× the largest self-serve plan so dragging to the top
  // surfaces the Enterprise ("contact sales") recommendation.
  const minutesMax = useMemo(() => Math.max(1000, Math.ceil((getAgentsMaxIncludedMinutes() * 1.5) / 1000) * 1000), []);
  const minutesSlider = useUsageSlider(DEFAULT_MINUTES_PER_MONTH, minutesMax);

  // RAG only matters past a sizeable knowledge base — below the threshold it's neither offered nor
  // applied (on/off prices are identical there anyway), so we hide the toggle and send `false`.
  const ragAvailable = kbSlider.value > RAG_MIN_PAGES;
  const effectiveRag = ragEnabled && ragAvailable;

  // Debounce the fetch-driving inputs as a single primitive key to avoid object churn.
  // Conversation minutes only scale the result client-side, so they don't trigger a re-fetch.
  const paramsKey = `${promptSlider.value}|${kbSlider.value}|${effectiveRag}`;
  const [debouncedKey] = useDebounceValue(paramsKey, DEBOUNCE_MS);

  // Skip the first fetch only when the server already provided a baseline; otherwise fetch on
  // mount so the calculator self-heals when the SSR baseline was empty (endpoint cold/unreachable).
  const isFirstRun = useRef(baselinePrices.length > 0);
  const requestId = useRef(0);

  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;
      return;
    }

    const [promptLength, numberOfPages, rag] = debouncedKey.split("|");
    const id = ++requestId.current;
    setIsLoading(true);

    calculateLlmPrices({
      promptLength: Number(promptLength),
      numberOfPages: Number(numberOfPages),
      ragEnabled: rag === "true",
    })
      .then(next => {
        if (id !== requestId.current) return; // a newer request superseded this one
        if (next.length > 0) {
          setPrices(next);
          setSelectedModel(current => current || pickDefaultModel(next));
        }
      })
      .catch(error => {
        logger.error(
          error instanceof Error ? error : null,
          "[AgentsCalculator] Failed to fetch LLM prices; keeping last known values",
        );
      })
      .finally(() => {
        if (id === requestId.current) setIsLoading(false);
      });
  }, [debouncedKey]);

  // Fall back to the cheapest featured model (not raw prices[0]) so the highlighted card always
  // matches a model the dropdown actually offers; prices[0] is a last resort if none are featured.
  const selectedEntry = prices.find(p => p.llm === selectedModel) ?? featuredByPrice(prices)[0] ?? prices[0];

  // Show the loading affordance from the moment a fetch-driving input changes (the debounce window)
  // through the in-flight request, so the LLM-cost line reads as "estimating" the whole time.
  // Conversation minutes recompute the cost client-side and intentionally don't set this.
  const isPricesUpdating = isLoading || paramsKey !== debouncedKey;

  const tabItems = [
    { id: "subscription", title: t("tab_subscription") },
    { id: "payg", title: t("tab_payg") },
  ];

  return (
    <div className={className}>
      <div className='tw-flex tw-flex-col tw-pt-4 lg:tw-pt-0 lg:tw-flex-row tw-gap-8 lg:tw-gap-16'>
        {/* Inputs */}
        <div className='tw-w-full tw-space-y-8 tw-order-2 lg:tw-order-1 lg:tw-w-1/2'>
          <div>
            <StepIndicator step={1} label={t("step_payment_model")} />
            <div className='tw-flex tw-flex-wrap tw-items-center tw-gap-3'>
              <Tabs selectedKey={calculatorMode} onSelectionChange={key => setCalculatorMode(key as CalculatorMode)}>
                <TabList items={tabItems} surface='cream' size='md' aria-label={t("payment_model_aria_label")} />
              </Tabs>
              {calculatorMode === "subscription" && <BillingCycleToggle size='md' />}
            </div>
          </div>

          <div>
            <StepIndicator step={2} label={t("step_configure")} />
            <div className='tw-space-y-6'>
              {/* Conversation minutes drive the recommended plan / monthly cost, so they stay visible. */}
              <div className='tw-space-y-3'>
                <FieldLabel>{t("minutes_label")}</FieldLabel>
                <UsageSlider
                  sliderValue={minutesSlider.sliderValue}
                  onSliderChange={minutesSlider.handleSliderChange}
                  maxValue={minutesMax}
                  unitSuffix={t("minutes_unit")}
                  ariaLabel={t("minutes_label")}
                />
              </div>

              {/* LLM-cost knobs — collapsed by default to keep the first-visit view simple. */}
              <AdvancedConfig label={t("advanced_config_label")}>
                <div className='tw-space-y-2'>
                  <FieldLabel>{t("model_label")}</FieldLabel>
                  <ModelSelect
                    prices={prices}
                    selectedModel={selectedModel}
                    onSelect={setSelectedModel}
                    ariaLabel={t("model_label")}
                    placeholder={t("model_placeholder")}
                    searchPlaceholder={t("model_search_placeholder")}
                    emptyLabel={t("model_search_empty")}
                    perMinuteSuffix={t("per_minute_suffix")}
                  />
                </div>

                <div className='tw-space-y-3'>
                  <FieldLabel>{t("prompt_length_label")}</FieldLabel>
                  <UsageSlider
                    sliderValue={promptSlider.sliderValue}
                    onSliderChange={promptSlider.handleSliderChange}
                    maxValue={PROMPT_LENGTH_MAX}
                    unitSuffix={t("prompt_length_unit")}
                    ariaLabel={t("prompt_length_label")}
                  />
                </div>

                <div className='tw-space-y-3'>
                  <FieldLabel>{t("kb_pages_label")}</FieldLabel>
                  <UsageSlider
                    sliderValue={kbSlider.sliderValue}
                    onSliderChange={kbSlider.handleSliderChange}
                    maxValue={KB_PAGES_MAX}
                    unitSuffix={t("kb_pages_unit")}
                    ariaLabel={t("kb_pages_label")}
                  />
                </div>

                {ragAvailable && (
                  <div className='tw-space-y-2'>
                    <RagToggle isSelected={ragEnabled} onChange={setRagEnabled} label={t("rag_label")} />
                    <p className='tw-px-1 tw-type-xs tw-text-gray-500'>{t("rag_caption")}</p>
                  </div>
                )}
              </AdvancedConfig>
            </div>

            <p className='tw-mt-4 tw-type-xs tw-text-gray-500'>{t("footnote")}</p>
          </div>
        </div>

        {/* Result */}
        <div className='tw-w-full tw-order-1 lg:tw-order-2 lg:tw-w-1/2'>
          <AgentsCalculatorResultCard
            pricePerMinute={selectedEntry?.pricePerMinute}
            modelName={selectedEntry ? modelLabel(selectedEntry.llm) : ""}
            minutesPerMonth={minutesSlider.value}
            mode={calculatorMode}
            period={billingCycle}
            isLoading={isPricesUpdating}
          />
        </div>
      </div>
    </div>
  );
}
